/**
 * Destino de "Compartir" de Android (Web Share Target del manifest). Chrome abre la PWA en
 * `/compartir?title=&text=&url=`; como el admin usa rutas con hash, ese path no lo ve el router.
 * Antes de arrancar Angular se guarda lo compartido y se redirige a `#/crm/leads/compartir`.
 */
const SHARE_PATH = '/compartir';
const STORAGE_KEY = 'smart-crm-share';

export interface SharedFile {
  name: string | null;
  type: string | null;
  content: string;
}

export interface SharedContent {
  title: string | null;
  text: string | null;
  url: string | null;
  /** Archivos compartidos por POST (contacto .vcf o chat exportado .txt), leídos por el service worker. */
  files?: SharedFile[];
}

/** Donde sw-master.js guarda lo compartido por POST. */
const SW_SHARE_CACHE = 'smart-crm-share';
const SW_SHARE_KEY = '/__shared-content';

export function captureShareTarget(): void {
  if (window.location.pathname.replace(/\/$/, '') !== SHARE_PATH) return;
  const params = new URLSearchParams(window.location.search);
  const shared: SharedContent = {
    title: params.get('title'),
    text: params.get('text'),
    url: params.get('url'),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(shared));
  } catch {
    // Sin sessionStorage (modo privado): se pierde el contenido, pero el alta manual sigue funcionando.
  }
  window.history.replaceState(null, '', '/#/crm/leads/compartir');
}

/**
 * Lo compartido por POST (service worker, permite archivos) o por GET (sessionStorage). Se borra al leerlo.
 */
export async function consumeSharedContentAsync(): Promise<SharedContent | null> {
  const fromGet = consumeSharedContent();
  if (fromGet) return fromGet;
  try {
    if (typeof caches === 'undefined') return null;
    const cache = await caches.open(SW_SHARE_CACHE);
    const response = await cache.match(SW_SHARE_KEY);
    if (!response) return null;
    await cache.delete(SW_SHARE_KEY);
    return (await response.json()) as SharedContent;
  } catch {
    return null;
  }
}

/** Devuelve lo compartido por GET una sola vez (se borra al leerlo). */
export function consumeSharedContent(): SharedContent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    return JSON.parse(raw) as SharedContent;
  } catch {
    return null;
  }
}

/**
 * Busca un teléfono en el texto compartido (p. ej. un mensaje reenviado o un contacto en texto):
 * +504 9999-8888, 50499998888 o 9999-8888. Devuelve el primero que encuentre.
 */
export function extractPhone(text: string | null | undefined): string | null {
  if (!text) return null;
  const match = text.match(/(\+?\d[\d\s().-]{6,17}\d)/g);
  if (!match) return null;
  for (const candidate of match) {
    const digits = candidate.replace(/\D/g, '');
    if (digits.length === 8 || (digits.length >= 10 && digits.length <= 15)) return candidate.trim();
  }
  return null;
}
